import {
  CalendarDays,
  Clock3,
  LockKeyhole,
  MapPin,
  Radar,
  ShieldCheck,
  Store,
  Truck,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import nordstromLogo from '@/assets/Nordstrom-Logo.png'
import macysLogo from '@/assets/Macys-Logo.png'
import lululemonLogo from '@/assets/Lululemon-Logo.png'
import zaraLogo from '@/assets/Zara-Logo.png'
import bloomingdalesLogo from '@/assets/Bloomingdales-Logo.png'
import ultaLogo from '@/assets/Ulta-Logo.png'
import { ROUTES } from '@/lib/navigation'

const benefits = [
  {
    icon: Clock3,
    title: 'Fast Same-Day Pickup',
    detail: 'Timing depends on distance, traffic, and driver availability.',
    tone: 'orange',
  },
  {
    icon: ShieldCheck,
    title: 'Trusted Delivery Network',
    detail: 'Orders are matched with available delivery providers.',
    tone: 'navy',
  },
  {
    icon: Store,
    title: 'Supported Local Stores',
    detail: 'Browse participating retailers and boutiques near you.',
    tone: 'navy',
  },
  {
    icon: MapPin,
    title: 'Live Delivery Updates',
    detail: 'Follow status when tracking is available for your order.',
    tone: 'navy',
  },
]

const steps = [
  {
    icon: Store,
    title: 'Pick your store',
    detail: 'Choose a supported local fashion retailer near you.',
  },
  {
    icon: CalendarDays,
    title: 'Schedule a pickup',
    detail: 'Tell us what needs to be collected and when.',
  },
  {
    icon: Truck,
    title: 'We deliver fast',
    detail: 'A delivery partner collects your order and brings it to you.',
  },
  {
    icon: MapPin,
    title: 'Track every step',
    detail: 'Follow progress from store to door when tracking is available.',
  },
]

const retailers = [
  { name: 'Nordstrom', logo: nordstromLogo },
  { name: "Macy's", logo: macysLogo },
  { name: 'Lululemon', logo: lululemonLogo },
  { name: 'Zara', logo: zaraLogo },
  { name: "Bloomingdale's", logo: bloomingdalesLogo },
  { name: 'Ulta Beauty', logo: ultaLogo },
]

export function PublicBenefitGrid() {
  return (
    <section className="public-benefits" aria-label="Style.re service benefits">
      {benefits.map(({ icon: Icon, title, detail, tone }) => (
        <article className="public-benefit-card" key={title}>
          <span className={`public-icon-disc public-icon-disc--${tone}`}>
            <Icon aria-hidden="true" />
          </span>
          <div>
            <h2>{title}</h2>
            <p>{detail}</p>
          </div>
        </article>
      ))}
    </section>
  )
}

export function TrustPaymentStrip() {
  return (
    <section className="public-trust-strip" aria-label="Checkout, payments, and tracking">
      <article>
        <span className="public-trust-strip__icon">
          <LockKeyhole aria-hidden="true" />
        </span>
        <div>
          <h2>Secure Checkout</h2>
          <p>Protected account and checkout flows.</p>
        </div>
      </article>
      <article>
        <span className="public-trust-strip__brand" aria-hidden="true">
          stripe
        </span>
        <div>
          <h2>Payments by Stripe</h2>
          <p>Payments are processed through Stripe.</p>
        </div>
      </article>
      <article>
        <span className="public-trust-strip__icon public-trust-strip__icon--orange">
          <Radar aria-hidden="true" />
        </span>
        <div>
          <h2>Live Tracking</h2>
          <p>Status and location updates when available.</p>
        </div>
      </article>
    </section>
  )
}

export function HowItWorksSection() {
  return (
    <section className="public-how" aria-labelledby="how-it-works-title">
      <div className="public-section-heading">
        <h2 id="how-it-works-title">How it works</h2>
        <span aria-hidden="true" />
      </div>
      <ol className="public-how__steps">
        {steps.map(({ icon: Icon, title, detail }, index) => (
          <li key={title} className="public-how__step">
            <span className="public-how__icon">
              <Icon aria-hidden="true" />
            </span>
            <div className="public-how__copy">
              <span className="public-how__number">{String(index + 1).padStart(2, '0')}</span>
              <h3>{title}</h3>
              <p>{detail}</p>
            </div>
          </li>
        ))}
      </ol>
    </section>
  )
}

export function RetailerLogoStrip() {
  return (
    <section className="public-retailers" aria-labelledby="retailer-strip-title">
      <p id="retailer-strip-title">We pick up from supported stores</p>
      <div className="public-retailers__logos">
        {retailers.map(({ name, logo }) => (
          <Link key={name} to={ROUTES.STORE_SELECTION} aria-label={`Browse ${name} locations`}>
            <img src={logo} alt={name} loading="lazy" />
          </Link>
        ))}
        <Link to={ROUTES.STORE_SELECTION} className="public-retailers__more">
          &amp; more boutiques
        </Link>
      </div>
      <small>
        Availability varies by service area. Retailer names identify pickup locations and do not
        imply a partnership.
      </small>
    </section>
  )
}
